import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClientError } from 'graphql-request';
import { fetchGql } from '@/lib/graphql_client';
import { MyOnboardingReadinessDocument } from '@/gql/graphql';

const INITIAL_INTERVAL_MS = 2_000;
const MAX_INTERVAL_MS = 30_000;
// After 5 completed polls the elapsed wall time exceeds the "less than a minute" promise
// (2s + 4s + 8s + 16s + 30s = 60s). Flip isStuck so the UI can surface an honest state.
const STUCK_AFTER_POLLS = 5;

/**
 * Poll myOnboardingReadiness with exponential backoff.
 * Only active when opts.enabled is true (i.e. new user with no contexts yet).
 * Backoff: 2s → 4s → 8s → 16s → 30s (capped). Uses a ref for the interval
 * so backoff progression does not cause additional re-renders.
 *
 * isStuck becomes true after STUCK_AFTER_POLLS completed polls (~60 s) while
 * ready is still false. Polling continues — the gate may eventually self-resolve
 * if the BE fixes the resolver — but the UI stops claiming "less than a minute".
 */
export function useOnboardingReadiness(opts: { enabled: boolean }) {
  const pollIntervalRef = useRef(INITIAL_INTERVAL_MS);
  const pollCountRef = useRef(0);
  const [isStuck, setIsStuck] = useState(false);

  const query = useQuery({
    queryKey: ['onboarding', 'readiness'],
    queryFn: async () => {
      const res = await fetchGql(MyOnboardingReadinessDocument, {});
      return res.myOnboardingReadiness;
    },
    enabled: opts.enabled,
    // Phase 2 can race with the gateway session write: switchContext resolves
    // (client-side active flips true) milliseconds before the session cookie is
    // readable, so the first poll may 401 even though the context is real.
    // Allow 2 retries on 401 with a short fixed backoff; a 3rd consecutive 401
    // is a genuine auth failure and is surfaced immediately. Other errors (network,
    // 5xx) get the standard 3 retries with exponential backoff.
    retry: (failureCount, error) => {
      if (error instanceof ClientError && error.response?.status === 401) return failureCount < 2;
      return failureCount < 3;
    },
    retryDelay: (failureCount, error) => {
      if (error instanceof ClientError && error.response?.status === 401) return 1_000;
      return Math.min(1_000 * 2 ** failureCount, 30_000);
    },
    refetchInterval: (q) => {
      if (q.state.data?.ready) return false;
      pollCountRef.current += 1;
      if (pollCountRef.current >= STUCK_AFTER_POLLS) setIsStuck(true);
      const current = pollIntervalRef.current;
      pollIntervalRef.current = Math.min(current * 2, MAX_INTERVAL_MS);
      return current;
    },
    staleTime: 0,
  });

  return {
    ready: query.data?.ready ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
    isStuck,
    refetch: query.refetch,
  };
}
